import { isValidElement, type ReactNode, useMemo, useState } from "react";
import { StyleSheet, View, type ViewProps, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { BaseText } from "~/components/layout/text";

import { Space } from "~/constants/sizing";
import { filled } from "~/util";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

export type Column = ReactNode | (() => ReactNode);

export type ColumnsProps = {
    items: Column[];
    columns?: number;
    keyExtractor?: (column: Column, index: number, columns: Column[]) => string;
    gap?: number;
    itemStyle?: StyleProp<ViewStyle>;
} & Omit<ViewProps, "children">;

export const Columns = ({ items, columns, keyExtractor, itemStyle, gap = Space.sm, ...viewProps }: ColumnsProps) => {
    const [width, setWidth] = useState(300);
    const handleOnLayout = setWidthOnLayout(setWidth, viewProps.onLayout);
    const total_items = items.length;
    const total_columns = Math.max(1, columns ?? items.length);
    const wrap = total_items > total_columns;
    const column_width = itemWidth(width, total_columns, gap);

    const renderables = useMemo(
        () =>
            items.filter(filled).map((column, index, columns) => ({
                key: keyExtractor?.(column, index, columns) ?? `column_${index}`,
                render:
                    typeof column === "function"
                        ? column
                        : () => (isValidElement(column) ? column : <BaseText>{column}</BaseText>),
            })),
        [items, keyExtractor],
    );

    return (
        <View
            {...viewProps}
            onLayout={handleOnLayout}
            style={[styles.container, wrap && styles.wrap, { gap }, viewProps.style]}
        >
            {renderables.map(({ key, render }) => (
                <View key={key} style={[styles.column, { width: column_width }, itemStyle]}>
                    {render()}
                </View>
            ))}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    wrap: {
        flexWrap: "wrap",
    },
    column: {
        justifyContent: "flex-start",
        alignItems: "stretch",
    },
});
